/**
 * Content Parser - Converts markdown + math content into structured blocks
 * Uses remark/rehype AST instead of regex preprocessing
 */

import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import type { Root, Content } from 'mdast';
import type { ContentBlock, ParsedContent, ListItem } from '@/types/contentBlocks';
import { parseChartConfig, isChartCodeBlock } from '@/utils/chartParser';

let blockIdCounter = 0;

const isProbablyInlineMath = (inner: string): boolean => {
  const trimmed = inner.trim();
  if (!trimmed) return false;

  if (/\n/.test(trimmed)) return false;

  if (/\\[a-zA-Z]+/.test(trimmed)) return true;
  if (/[_^{}=<>]/.test(trimmed)) return true;
  if (/(\d|\))\s*[-+*/]/.test(trimmed)) return true;
  if (/[-+*/]\s*(\d|\()/.test(trimmed)) return true;

  if (!/\s/.test(trimmed) && /^[A-Za-z0-9]+$/.test(trimmed)) return true;

  if (/^\s*[\d.,]+\s*(USD|USDT|USDC|EUR|INR|GBP|JPY|AUD|CAD|CHF|CNY)\b/i.test(trimmed)) return false;
  if (/[A-Za-z]{2,}/.test(trimmed) && /\s/.test(trimmed)) return false;

  return false;
};

export const escapeNonMathDollars = (text: string): string => {
  if (!text || typeof text !== 'string') return text;

  let out = '';
  let i = 0;

  const findNextSingleDollar = (start: number): number => {
    for (let k = start; k < text.length; k++) {
      const ch = text[k];
      if (ch === '\\' && text[k + 1] === '$') {
        k += 1;
        continue;
      }
      if (ch === '$') {
        if (text[k + 1] === '$') {
          k += 1;
          continue;
        }
        return k;
      }
    }
    return -1;
  };

  while (i < text.length) {
    const ch = text[i];

    if (ch === '\\' && text[i + 1] === '$') {
      out += '\\$';
      i += 2;
      continue;
    }

    if (ch !== '$') {
      out += ch;
      i += 1;
      continue;
    }

    if (text[i + 1] === '$') {
      out += '$$';
      i += 2;
      continue;
    }

    const j = findNextSingleDollar(i + 1);
    if (j === -1) {
      out += '$';
      i += 1;
      continue;
    }

    const inner = text.slice(i + 1, j);
    if (isProbablyInlineMath(inner)) {
      out += `$${inner}$`;
    } else {
      out += inner;
    }

    i = j + 1;
  }

  return out;
};

const generateBlockId = (): string => {
  return `block-${Date.now()}-${blockIdCounter++}`;
};

/**
 * Detect if content is a meme component
 */
const detectMeme = (content: string): any | null => {
  try {
    if (content.includes('MEME_COMPONENT:') || content.includes('memeData')) {
      const memeMatch = content.match(/MEME_COMPONENT:\s*({[\s\S]*?})\s*$/);
      if (memeMatch) {
        return JSON.parse(memeMatch[1]);
      }
    }
    // Also support direct JSON payloads from agents, e.g. {"type":"meme-result", ...}
    const trimmed = content.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const obj = JSON.parse(trimmed);
        if (obj && (obj.type === 'meme-templates' || obj.type === 'meme-result' || obj.type === 'meme-error')) {
          return obj;
        }
      } catch (_) {
        // Not valid JSON or not a meme payload
      }
    }
  } catch (e) {
    // Not a meme
  }
  return null;
};

/**
 * Detect if content is a structured list component (Google Search Result)
 */
const detectStructuredList = (content: string): any | null => {
  try {
    const trimmed = content.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      const obj = JSON.parse(trimmed);
      if (obj && obj.type === 'list' && Array.isArray(obj.list)) {
        return obj;
      }
    }
  } catch (e) {
    // Not a structured list
  }
  return null;
};

/**
 * Preprocess content to normalize LaTeX and chemical formulas
 */
const preprocessMathContent = (text: string): string => {
  let processed = text;

  // Convert LaTeX delimiters
  processed = processed.replace(/\\\(/g, '$');
  processed = processed.replace(/\\\)/g, '$');
  processed = processed.replace(/\\\[/g, '$$');
  processed = processed.replace(/\\\]/g, '$$');

  // Fix incomplete math expressions (for streaming)
  processed = processed.replace(/\\frac\{([^}]*?)(?:\s*$)/g, '\\frac{$1}{1}');
  processed = processed.replace(/\\sqrt\{([^}]*?)(?:\s*$)/g, '\\sqrt{$1}');
  processed = processed.replace(/\\ce\{([^}]*?)(?:\s*$)/g, '\\ce{$1}');

  // Clean up nested chemical formulas
  processed = processed.replace(/\$\\ce\{([^$]*?)\$\\ce\{([^}]*?)\}\$([^}]*?)\}\$/g, (match, before, middle, after) => {
    const combined = `${before}${middle}${after}`.replace(/\$+/g, '').replace(/\s+/g, ' ').trim();
    return `$\\ce{${combined}}$`;
  });

  const parts = processed.split(/(```[\s\S]*?```)/g);
  processed = parts
    .map((seg) => (seg.startsWith('```') ? seg : escapeNonMathDollars(seg)))
    .join('');

  return processed;
};


/**
 * Convert MDAST node to ContentBlock
 */
const convertNode = (node: Content, parent?: any): ContentBlock | ContentBlock[] | null => {
  switch (node.type) {
    case 'paragraph': {
      const textContent = extractTextFromNode(node);
      return {
        type: 'paragraph',
        id: generateBlockId(),
        content: textContent,
      };
    }

    case 'heading': {
      const textContent = extractTextFromNode(node);
      return {
        type: 'heading',
        id: generateBlockId(),
        level: node.depth as 1 | 2 | 3 | 4 | 5 | 6,
        content: textContent,
      };
    }

    case 'code': {
      const lang = node.lang || '';
      const code = node.value || '';

      const lowerLang = lang.toLowerCase();

      // Detect file-creation blocks
      if (lowerLang === 'file-creation') {
        try {
          // Sanitize raw newlines, carriage returns and tabs inside JSON string literals
          const sanitizedCode = code.replace(/"([^"\\]|\\.)*"/g, (match) => {
            return match
              .replace(/\r/g, '\\r')
              .replace(/\n/g, '\\n')
              .replace(/\t/g, '\\t');
          });
          const parsed = JSON.parse(sanitizedCode);
          return {
            type: 'file-creation',
            id: generateBlockId(),
            fileType: parsed.type || 'pdf',
            filename: parsed.filename || 'document.pdf',
            title: parsed.title || 'Downloadable File',
            content: parsed.content || '',
          } as any;
        } catch (e) {
          console.error("Failed to parse file-creation code block:", e);
          // Fallback to normal code block so the user can still see the text if JSON is malformed
          return {
            type: 'code',
            id: generateBlockId(),
            language: 'json',
            code: code,
            showLineNumbers: true,
          };
        }
      }

      // Only treat as chart if:
      // 1) The code block language is explicitly chart-related, OR
      // 2) The JSON includes an explicit chart intent flag (e.g., "chart": true or "visualization": true)
      const hasExplicitChartFlag = /\b("chart"|"visualization")\s*:\s*(true|"true")/i.test(code);
      if (isChartCodeBlock(lowerLang) || (lowerLang === 'json' && hasExplicitChartFlag)) {
        const chartFromCode = parseChartConfig(code);
        if (chartFromCode) {
          return {
            type: 'chart',
            id: generateBlockId(),
            config: chartFromCode.config,
            rawSpec: chartFromCode.rawSpec,
          } as ContentBlock;
        }
      }

      // Detect math/chemistry code blocks
      if (lang === 'math' || lang === 'latex') {
        return {
          type: 'math',
          id: generateBlockId(),
          content: code,
          displayMode: true,
        };
      }

      if (lang === 'chem' || code.includes('\\ce{')) {
        return {
          type: 'chemistry',
          id: generateBlockId(),
          formula: code,
          displayMode: true,
        };
      }

      return {
        type: 'code',
        id: generateBlockId(),
        language: lang,
        code: code,
        showLineNumbers: true,
      };
    }

    case 'math': {
      const formula = node.value || '';

      // Detect chemistry formulas
      if (formula.includes('\\ce{')) {
        return {
          type: 'chemistry',
          id: generateBlockId(),
          formula: formula,
          displayMode: (node as any).displayMode || false,
        };
      }

      return {
        type: 'math',
        id: generateBlockId(),
        content: formula,
        displayMode: (node as any).displayMode || false,
      };
    }

    case 'inlineMath': {
      const formula = node.value || '';

      if (formula.includes('\\ce{')) {
        return {
          type: 'chemistry',
          id: generateBlockId(),
          formula: formula,
          displayMode: false,
        };
      }

      return {
        type: 'math',
        id: generateBlockId(),
        content: formula,
        displayMode: false,
      };
    }

    case 'table': {
      const headers: string[] = [];
      const rows: string[][] = [];
      const align = node.align || [];

      // Extract headers
      if (node.children[0]?.type === 'tableRow') {
        node.children[0].children.forEach((cell: any) => {
          headers.push(extractTextFromNode(cell));
        });
      }

      // Extract rows
      for (let i = 1; i < node.children.length; i++) {
        const row = node.children[i];
        if (row.type === 'tableRow') {
          const rowData: string[] = [];
          row.children.forEach((cell: any) => {
            rowData.push(extractTextFromNode(cell));
          });
          rows.push(rowData);
        }
      }

      return {
        type: 'table',
        id: generateBlockId(),
        headers,
        rows,
        align: align as ('left' | 'center' | 'right')[],
      };
    }

    case 'list': {
      const items: ListItem[] = node.children.map((item: any) => {
        return {
          content: extractTextFromNode(item),
          children: undefined, // TODO: Handle nested lists
        };
      });

      return {
        type: 'list',
        id: generateBlockId(),
        ordered: node.ordered || false,
        items,
        start: node.start,
      };
    }

    case 'blockquote': {
      const content = extractTextFromNode(node);
      return {
        type: 'blockquote',
        id: generateBlockId(),
        content,
      };
    }

    case 'thematicBreak': {
      return {
        type: 'hr',
        id: generateBlockId(),
      };
    }

    case 'html': {
      return {
        type: 'html',
        id: generateBlockId(),
        content: node.value || '',
        sanitized: false,
      };
    }

    case 'image': {
      return {
        type: 'image',
        id: generateBlockId(),
        url: node.url || '',
        alt: node.alt || '',
        title: node.title || undefined,
      };
    }

    default:
      return null;
  }
};

/**
 * Extract text content from a node recursively
 */
const extractTextFromNode = (node: any): string => {
  if (typeof node === 'string') return node;
  if (!node) return '';

  if (node.type === 'text') return node.value || '';
  if (node.type === 'inlineCode') return `\`${node.value || ''}\``;
  if (node.type === 'strong') {
    const text = node.children?.map(extractTextFromNode).join('') || '';
    // Don't wrap with ** if already contains markdown formatting
    return text;
  }
  if (node.type === 'emphasis') {
    const text = node.children?.map(extractTextFromNode).join('') || '';
    // Don't wrap with * if already contains markdown formatting
    return text;
  }
  if (node.type === 'link') {
    const text = node.children?.map(extractTextFromNode).join('') || '';
    return `[${text}](${node.url || ''})`;
  }
  if (node.type === 'inlineMath') {
    return `$${node.value || ''}$`;
  }
  if (node.type === 'math') {
    return `$$${node.value || ''}$$`;
  }

  if (node.children) {
    return node.children.map(extractTextFromNode).join('');
  }

  return '';
};

/**
 * Main parser function
 */
export const parseContent = async (content: string): Promise<ParsedContent> => {
  // Check for meme first
  const memeData = detectMeme(content);
  if (memeData) {
    return {
      blocks: [{
        type: 'meme',
        id: generateBlockId(),
        data: memeData,
      }],
      metadata: {
        hasCode: false,
        hasMath: false,
        hasChemistry: false,
        hasTables: false,
        hasCharts: false,
      },
    };
  }

  // Check for structured list
  const structuredList = detectStructuredList(content);
  if (structuredList) {
    return {
      blocks: [{
        type: 'list',
        id: generateBlockId(),
        ordered: false,
        items: structuredList.list.map((item: any) => ({
          content: item.snippet || item.title || '',
          reference_indexes: item.reference_indexes || []
        })),
        start: 1,
      }],
      metadata: {
        hasCode: false,
        hasMath: false,
        hasChemistry: false,
        hasTables: false,
        hasCharts: false,
      },
    };
  }

  // Preprocess math content
  const preprocessed = preprocessMathContent(content);

  // Parse with unified/remark
  const processor = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkMath);

  const tree = processor.parse(preprocessed) as Root;

  // Convert AST to blocks
  const blocks: ContentBlock[] = [];
  let hasCode = false;
  let hasMath = false;
  let hasChemistry = false;
  let hasTables = false;
  let hasCharts = false;

  tree.children.forEach((node) => {
    const block = convertNode(node);
    if (block) {
      if (Array.isArray(block)) {
        blocks.push(...block);
      } else {
        blocks.push(block);

        // Update metadata
        if (block.type === 'code') hasCode = true;
        if (block.type === 'math') hasMath = true;
        if (block.type === 'chemistry') hasChemistry = true;
        if (block.type === 'table') hasTables = true;
        if (block.type === 'chart') hasCharts = true;
      }
    }
  });

  return {
    blocks,
    metadata: {
      hasCode,
      hasMath,
      hasChemistry,
      hasTables,
      hasCharts,
    },
  };
};
/**
 * Synchronous parser for streaming (progressive parsing)
 */
export const parseContentSync = (content: string): ParsedContent => {
  // Check for meme first
  const memeData = detectMeme(content);
  if (memeData) {
    return {
      blocks: [{
        type: 'meme',
        id: generateBlockId(),
        data: memeData,
      }],
      metadata: {
        hasCode: false,
        hasMath: false,
        hasChemistry: false,
        hasTables: false,
        hasCharts: false,
      },
    };
  }

  // Check for structured list
  const structuredList = detectStructuredList(content);
  if (structuredList) {
    return {
      blocks: [{
        type: 'list',
        id: generateBlockId(),
        ordered: false,
        items: structuredList.list.map((item: any) => ({
          content: item.snippet || item.title || '',
          reference_indexes: item.reference_indexes || []
        })),
        start: 1,
      }],
      metadata: {
        hasCode: false,
        hasMath: false,
        hasChemistry: false,
        hasTables: false,
        hasCharts: false,
      },
    };
  }

  // Preprocess math content
  const preprocessed = preprocessMathContent(content);

  // Parse with unified/remark (synchronously)
  const processor = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkMath);

  const tree = processor.parse(preprocessed) as Root;

  // Convert AST to blocks
  const blocks: ContentBlock[] = [];
  let hasCode = false;
  let hasMath = false;
  let hasChemistry = false;
  let hasTables = false;
  let hasCharts = false;

  tree.children.forEach((node) => {
    const block = convertNode(node);
    if (block) {
      if (Array.isArray(block)) {
        blocks.push(...block);
      } else {
        blocks.push(block);

        // Update metadata
        if (block.type === 'code') hasCode = true;
        if (block.type === 'math') hasMath = true;
        if (block.type === 'chemistry') hasChemistry = true;
        if (block.type === 'table') hasTables = true;
        if (block.type === 'chart') hasCharts = true;
      }
    }
  });

  return {
    blocks,
    metadata: {
      hasCode,
      hasMath,
      hasChemistry,
      hasTables,
      hasCharts,
    },
  };
};
